<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import PublicHeader from "@/components/public/PublicHeader.vue";
import PublicFooter from "@/components/public/PublicFooter.vue";

const { t } = useI18n();

const router = useRouter();

const handleHelpClick = () => {
  console.log("Help button clicked");
};

const handleLanguageChange = (language: string) => {
  console.log("Language changed to:", language);
};

const handleFooterLinkClick = (section: string, item: string) => {
  console.log("Footer link clicked:", section, item);
};

const handleRegister = () => {
  console.log("Trader registration initiated");
};
</script>

<template>
  <PublicHeader
    @help-click="handleHelpClick"
    @language-change="handleLanguageChange"
  />

  <main id="main-content" class="min-h-screen">
    <section
      id="breadcrumb-section"
      class="bg-white border-b border-gray-200 py-4"
    >
      <div class="max-w-4xl mx-auto px-5">
        <nav class="flex items-center space-x-2 text-sm">
          <span class="text-primary hover:text-primary/80 cursor-pointer"
            >{{ $t('trader.register_page.breadcrumb.home') }}</span
          >
          <i class="fas fa-chevron-right text-gray-400 text-xs"></i>
          <span class="text-gray-600">{{ $t('trader.register_page.breadcrumb.registration') }}</span>
          <i class="fas fa-chevron-right text-gray-400 text-xs"></i>
          <span class="text-accent font-medium">{{ $t('trader.register_page.breadcrumb.credit_holder_trader') }}</span>
        </nav>
      </div>
    </section>

    <section id="registration-form-section" class="py-12">
      <div class="max-w-4xl mx-auto px-5">
        <div id="form-header" class="text-center mb-8">
          <div
            class="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <i class="fas fa-exchange-alt text-accent text-2xl"></i>
          </div>
          <h1 class="text-3xl font-bold text-accent mb-2">
            {{ $t('trader.register_page.header.title') }}
          </h1>
          <p class="text-lg text-gray-600">
            {{ $t('trader.register_page.header.subtitle') }}
          </p>
        </div>

        <div
          id="registration-form-card"
          class="bg-white rounded-lg border border-gray-200 shadow-sm"
        >
          <div class="p-8">
            <form id="trader-registration-form">
              <div id="personal-info-section" class="mb-8">
                <h2
                  class="text-xl font-semibold text-accent mb-6 flex items-center"
                >
                  <i class="fas fa-user text-primary mr-3"></i>
                  {{ $t('trader.register_page.personal_info.title') }}
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div id="name-field">
                    <label class="block text-sm font-medium text-gray-700 mb-2"
                      >{{ $t('trader.register_page.personal_info.full_name_org') }} *</label
                    >
                    <input
                      type="text"
                      class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      :placeholder="$t('trader.register_page.personal_info.full_name_placeholder')"
                    />
                  </div>

                  <div id="country-field">
                    <label class="block text-sm font-medium text-gray-700 mb-2"
                      >{{ $t('trader.register_page.personal_info.country') }} *</label
                    >
                    <select
                      class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">{{ $t('trader.register_page.personal_info.select_country') }}</option>
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="GB">United Kingdom</option>
                      <option value="DE">Germany</option>
                      <option value="FR">France</option>
                      <option value="AU">Australia</option>
                      <option value="JP">Japan</option>
                      <option value="BR">Brazil</option>
                      <option value="IN">India</option>
                      <option value="CN">China</option>
                    </select>
                  </div>

                  <div id="email-field">
                    <label class="block text-sm font-medium text-gray-700 mb-2"
                      >{{ $t('trader.register_page.personal_info.email_address') }} *</label
                    >
                    <input
                      type="email"
                      class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      :placeholder="$t('trader.register_page.personal_info.email_placeholder')"
                    />
                  </div>

                  <div id="phone-field">
                    <label class="block text-sm font-medium text-gray-700 mb-2"
                      >{{ $t('trader.register_page.personal_info.phone_number') }}</label
                    >
                    <input
                      type="tel"
                      class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      :placeholder="$t('trader.register_page.personal_info.phone_placeholder')"
                    />
                  </div>
                </div>
              </div>

              <div id="intended-use-section" class="mb-8">
                <h2
                  class="text-xl font-semibold text-accent mb-6 flex items-center"
                >
                  <i class="fas fa-clipboard-list text-primary mr-3"></i>
                  {{ $t('trader.register_page.intended_use.title') }}
                </h2>

                <div id="use-options" class="space-y-4">
                  <div class="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="buy-credits"
                      class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div>
                      <label
                        for="buy-credits"
                        class="text-sm font-medium text-gray-700"
                        >{{ $t('trader.register_page.intended_use.buy_credits.title') }}</label
                      >
                      <p class="text-xs text-gray-500 mt-1">
                        {{ $t('trader.register_page.intended_use.buy_credits.description') }}
                      </p>
                    </div>
                  </div>

                  <div class="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="trade-credits"
                      class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div>
                      <label
                        for="trade-credits"
                        class="text-sm font-medium text-gray-700"
                        >{{ $t('trader.register_page.intended_use.trade_credits.title') }}</label
                      >
                      <p class="text-xs text-gray-500 mt-1">
                        {{ $t('trader.register_page.intended_use.trade_credits.description') }}
                      </p>
                    </div>
                  </div>

                  <div class="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="retire-credits"
                      class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div>
                      <label
                        for="retire-credits"
                        class="text-sm font-medium text-gray-700"
                        >{{ $t('trader.register_page.intended_use.retire_credits.title') }}</label
                      >
                      <p class="text-xs text-gray-500 mt-1">
                        {{ $t('trader.register_page.intended_use.retire_credits.description') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div id="account-security-section" class="mb-8">
                <h2
                  class="text-xl font-semibold text-accent mb-6 flex items-center"
                >
                  <i class="fas fa-shield-alt text-primary mr-3"></i>
                  {{ $t('trader.register_page.account_security.title') }}
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div id="password-field">
                    <label class="block text-sm font-medium text-gray-700 mb-2"
                      >{{ $t('trader.register_page.account_security.password') }} *</label
                    >
                    <input
                      type="password"
                      class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      :placeholder="$t('trader.register_page.account_security.password_placeholder')"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      {{ $t('trader.register_page.account_security.password_requirements') }}
                    </p>
                  </div>

                  <div id="confirm-password-field">
                    <label class="block text-sm font-medium text-gray-700 mb-2"
                      >{{ $t('trader.register_page.account_security.confirm_password') }} *</label
                    >
                    <input
                      type="password"
                      class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      :placeholder="$t('trader.register_page.account_security.confirm_password_placeholder')"
                    />
                  </div>
                </div>
              </div>

              <div id="terms-section" class="mb-8">
                <h2
                  class="text-xl font-semibold text-accent mb-6 flex items-center"
                >
                  <i class="fas fa-file-contract text-primary mr-3"></i>
                  {{ $t('trader.register_page.terms_declarations.title') }}
                </h2>

                <div class="space-y-4">
                  <div class="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="terms-conditions"
                      class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div>
                      <label
                        for="terms-conditions"
                        class="text-sm text-gray-700"
                        >{{ $t('trader.register_page.terms_declarations.agree_terms') }}
                        <span
                          class="text-primary hover:underline cursor-pointer"
                          >{{ $t('trader.register_page.terms_declarations.terms_conditions') }}</span
                        >
                        *</label
                      >
                    </div>
                  </div>

                  <div class="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="privacy-policy"
                      class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div>
                      <label for="privacy-policy" class="text-sm text-gray-700"
                        >{{ $t('trader.register_page.terms_declarations.acknowledge_privacy') }}
                        <span
                          class="text-primary hover:underline cursor-pointer"
                          >{{ $t('trader.register_page.terms_declarations.privacy_policy') }}</span
                        >
                        *</label
                      >
                    </div>
                  </div>

                  <div class="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="registry-rules"
                      class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div>
                      <label for="registry-rules" class="text-sm text-gray-700"
                        >{{ $t('trader.register_page.terms_declarations.comply_rules') }} *</label
                      >
                    </div>
                  </div>

                  <div class="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="marketing-consent"
                      class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div>
                      <label
                        for="marketing-consent"
                        class="text-sm text-gray-700"
                        >{{ $t('trader.register_page.terms_declarations.marketing_consent') }}</label
                      >
                    </div>
                  </div>
                </div>
              </div>

              <div
                id="form-actions"
                class="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200"
              >
                <button
                  type="submit"
                  class="flex-1 bg-accent text-white py-3 px-6 rounded-md font-medium hover:bg-accent/90 transition-colors"
                >
                  <i class="fas fa-user-plus mr-2"></i>
                  {{ $t('trader.register_page.form_actions.create_account') }}
                </button>
                <button
                  type="button"
                  class="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-md font-medium hover:bg-gray-50 transition-colors"
                >
                  <i class="fas fa-times mr-2"></i>
                  {{ $t('trader.register_page.form_actions.cancel') }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div
          id="registration-info"
          class="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <div class="flex items-start space-x-3">
            <i class="fas fa-info-circle text-blue-600 text-xl mt-1"></i>
            <div>
              <h3 class="text-lg font-semibold text-blue-800 mb-2">
                {{ $t('trader.register_page.registration_info.title') }}
              </h3>
              <ul class="text-blue-700 space-y-1 text-sm">
                <li>• {{ $t('trader.register_page.registration_info.free_registration') }}</li>
                <li>
                  • {{ $t('trader.register_page.registration_info.unique_rin') }}
                </li>
                <li>
                  • {{ $t('trader.register_page.registration_info.email_verification') }}
                </li>
                <li>• {{ $t('trader.register_page.registration_info.approval_time') }}</li>
                <li>
                  • {{ $t('trader.register_page.registration_info.start_trading') }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>

  <PublicFooter @link-click="handleFooterLinkClick" />
</template>
